import React, { useState } from 'react';
import { troubleshooting } from '../mockData/mock';
import { AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Troubleshooting = () => {
  const [expandedIssues, setExpandedIssues] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(troubleshooting.map(item => item.category))];

  const filteredIssues = selectedCategory === 'all'
    ? troubleshooting
    : troubleshooting.filter(item => item.category === selectedCategory);

  const toggleIssue = (id) => {
    setExpandedIssues(prev =>
      prev.includes(id)
        ? prev.filter(issueId => issueId !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#a8b5a0]/10 px-4 py-2 rounded-full mb-6">
            <AlertCircle className="h-5 w-5 text-[#a8b5a0]" />
            <span className="text-sm font-medium text-[#736c64]">Problem Solving</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Troubleshooting Guide
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            Running into issues? You're not alone. Here are solutions to the most common sourdough challenges.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-[#e8e6e1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`cursor-pointer transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#a8b5a0] hover:bg-[#8fa087] text-white'
                    : 'border-[#e8e6e1] text-[#736c64] hover:bg-[#f7f5f2]'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? 'All Issues' : category}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Issues List */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {filteredIssues.map((issue) => (
              <Card key={issue.id} className="overflow-hidden hover:shadow-md transition-shadow">
                <button
                  onClick={() => toggleIssue(issue.id)}
                  className="w-full text-left"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2">
                          <AlertCircle className="h-5 w-5 text-[#a8b5a0] flex-shrink-0" />
                          <h3 className="text-lg font-semibold text-[#1e1919]">
                            {issue.issue}
                          </h3>
                        </div>
                        <Badge variant="outline" className="text-xs border-[#e8e6e1] text-[#736c64]">
                          {issue.category}
                        </Badge>
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 text-[#736c64] flex-shrink-0 transition-transform ${
                          expandedIssues.includes(issue.id) ? 'rotate-180' : ''
                        }`}
                      />
                    </div>

                    {expandedIssues.includes(issue.id) && (
                      <div className="mt-6 pt-6 border-t border-[#e8e6e1] animate-in fade-in slide-in-from-top duration-200">
                        <h4 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-[#a8b5a0]" />
                          Solutions
                        </h4>
                        <ul className="space-y-3">
                          {issue.solutions.map((solution, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-[#a8b5a0]/20 rounded-full flex items-center justify-center text-[#a8b5a0] font-semibold text-sm mt-0.5">
                                {index + 1}
                              </span>
                              <span className="text-[#736c64] leading-relaxed pt-0.5">
                                {solution}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </button>
              </Card>
            ))}
          </div>

          {filteredIssues.length === 0 && (
            <div className="text-center py-16">
              <AlertCircle className="h-16 w-16 text-[#e8e6e1] mx-auto mb-4" />
              <p className="text-lg text-[#736c64]">
                No issues found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Additional Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#a8b5a0]/10 to-transparent rounded-2xl p-8 border border-[#e8e6e1]">
            <h2 className="text-2xl font-bold text-[#1e1919] mb-4">
              Still Need Help?
            </h2>
            <p className="text-[#736c64] mb-6">
              Can't find a solution to your issue? Check out our FAQ or get in touch with us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/faq"
                className="inline-flex items-center justify-center px-6 py-3 bg-[#a8b5a0] hover:bg-[#8fa087] text-white rounded-lg font-medium transition-colors"
              >
                View FAQ
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#e8e6e1] hover:bg-white text-[#736c64] rounded-lg font-medium transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Troubleshooting;
